/**
 * Admin Products Service
 * API client for admin product management endpoints
 */

import httpClient from '../../../../apiService/httpClient';
import { IAdminGeogridProduct, IAdminDrainageProduct, IAdminGeogridProductRequest, IAdminDrainageProductRequest, IGeogridProductsResponse, IDrainageProductsResponse } from '../types/AdminTypes';

// Base URL for admin API
const API_BASE = process.env.REACT_APP_API_BASEURL || 'http://localhost:3004';
const ADMIN_API_PATH = '/api/admin/products';

// Backend API response type (nested lcrEquation)
interface IBackendGeogridProduct extends Omit<IAdminGeogridProduct, 'lcrEquationM2' | 'lcrEquationM1' | 'lcrEquationC' | 'lcrEquationThreshold'> {
    lcrEquation?: { m2: number; m1: number; c: number; threshold: number } | null;
}

interface IBackendGeogridProductsResponse {
    type: 'geogrid';
    count: number;
    products: IBackendGeogridProduct[];
}

/**
 * Flatten nested lcrEquation from backend into flat fields for frontend
 */
const flattenLcrEquation = (product: IBackendGeogridProduct): IAdminGeogridProduct => {
    const { lcrEquation, ...rest } = product;
    return {
        ...rest,
        lcrEquationM2: lcrEquation?.m2 ?? null,
        lcrEquationM1: lcrEquation?.m1 ?? null,
        lcrEquationC: lcrEquation?.c ?? null,
        lcrEquationThreshold: lcrEquation?.threshold ?? null,
    } as IAdminGeogridProduct;
};

/**
 * Nest flat LCR fields back into lcrEquation object for backend API
 */
const nestLcrEquation = (request: IAdminGeogridProductRequest): Record<string, unknown> => {
    const { lcrEquationM2, lcrEquationM1, lcrEquationC, lcrEquationThreshold, ...rest } = request;
    const hasAllLcrValues = lcrEquationM2 != null && lcrEquationM1 != null && lcrEquationC != null && lcrEquationThreshold != null;
    return {
        ...rest,
        lcrEquation: hasAllLcrValues ? {
            m2: lcrEquationM2,
            m1: lcrEquationM1,
            c: lcrEquationC,
            threshold: lcrEquationThreshold,
        } : null,
    };
};

/**
 * Admin Products Service - API client for product management
 */
export class AdminProductsService {
    // ============= Geogrid Products =============

    /**
     * Get all geogrid products
     */
    static async getGeogridProducts(): Promise<IGeogridProductsResponse> {
        const response = await httpClient.getAsync<IBackendGeogridProductsResponse>({
            url: `${ADMIN_API_PATH}/geogrid`,
            baseURL: API_BASE,
        });
        return {
            ...response.data,
            products: response.data.products.map(flattenLcrEquation),
        };
    }

    /**
     * Get a single geogrid product by ID
     */
    static async getGeogridProduct(id: string): Promise<IAdminGeogridProduct> {
        const response = await httpClient.getAsync<IBackendGeogridProduct>({
            url: `${ADMIN_API_PATH}/geogrid/${id}`,
            baseURL: API_BASE,
        });
        return flattenLcrEquation(response.data);
    }

    /**
     * Create a new geogrid product
     */
    static async createGeogridProduct(product: IAdminGeogridProductRequest): Promise<IAdminGeogridProduct> {
        const response = await httpClient.postAsync<Record<string, unknown>, IBackendGeogridProduct>({
            url: `${ADMIN_API_PATH}/geogrid`,
            baseURL: API_BASE,
            data: nestLcrEquation(product),
        });
        return flattenLcrEquation(response.data);
    }

    /**
     * Update an existing geogrid product
     */
    static async updateGeogridProduct(id: string, product: IAdminGeogridProductRequest): Promise<IAdminGeogridProduct> {
        const response = await httpClient.putAsync<Record<string, unknown>, IBackendGeogridProduct>({
            url: `${ADMIN_API_PATH}/geogrid/${id}`,
            baseURL: API_BASE,
            data: nestLcrEquation(product),
        });
        return flattenLcrEquation(response.data);
    }

    /**
     * Delete a geogrid product
     */
    static async deleteGeogridProduct(id: string): Promise<void> {
        await httpClient.deleteAsync({
            url: `${ADMIN_API_PATH}/geogrid/${id}`,
            baseURL: API_BASE,
        });
    }

    /**
     * Get all geogrid families
     */
    static async getGeogridFamilies(): Promise<string[]> {
        const response = await httpClient.getAsync<string[]>({
            url: `${ADMIN_API_PATH}/geogrid/families`,
            baseURL: API_BASE,
        });
        return response.data;
    }

    // ============= Drainage Products =============

    /**
     * Get all drainage products
     */
    static async getDrainageProducts(): Promise<IDrainageProductsResponse> {
        const response = await httpClient.getAsync<IDrainageProductsResponse>({
            url: `${ADMIN_API_PATH}/drainage`,
            baseURL: API_BASE,
        });
        return response.data;
    }

    /**
     * Get a single drainage product by ID
     */
    static async getDrainageProduct(id: string): Promise<IAdminDrainageProduct> {
        const response = await httpClient.getAsync<IAdminDrainageProduct>({
            url: `${ADMIN_API_PATH}/drainage/${id}`,
            baseURL: API_BASE,
        });
        return response.data;
    }

    /**
     * Create a new drainage product
     */
    static async createDrainageProduct(product: IAdminDrainageProductRequest): Promise<IAdminDrainageProduct> {
        const response = await httpClient.postAsync<IAdminDrainageProductRequest, IAdminDrainageProduct>({
            url: `${ADMIN_API_PATH}/drainage`,
            baseURL: API_BASE,
            data: product,
        });
        return response.data;
    }

    /**
     * Update an existing drainage product
     */
    static async updateDrainageProduct(id: string, product: IAdminDrainageProductRequest): Promise<IAdminDrainageProduct> {
        const response = await httpClient.putAsync<IAdminDrainageProductRequest, IAdminDrainageProduct>({
            url: `${ADMIN_API_PATH}/drainage/${id}`,
            baseURL: API_BASE,
            data: product,
        });
        return response.data;
    }

    /**
     * Delete a drainage product
     */
    static async deleteDrainageProduct(id: string): Promise<void> {
        await httpClient.deleteAsync({
            url: `${ADMIN_API_PATH}/drainage/${id}`,
            baseURL: API_BASE,
        });
    }

    /**
     * Get all drainage categories
     */
    static async getDrainageCategories(): Promise<string[]> {
        const response = await httpClient.getAsync<string[]>({
            url: `${ADMIN_API_PATH}/drainage/categories`,
            baseURL: API_BASE,
        });
        return response.data;
    }
}
